import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './dialog';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from './button';
import { Badge } from './badge';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import Icon from './icon';
import { orderApi } from '../../services/orderApi';
import { Order, getOrderStatusText, getOrderStatusColor, formatCurrency, formatOrderDate, isOrderPendingOrProcessing } from '../../types/order';

interface OrderDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string | null;
  outletId: string;
  onOrderUpdated?: () => void; // Callback để refresh danh sách đơn hàng
}

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
  isOpen,
  onClose,
  orderId,
  outletId,
  onOrderUpdated
}) => {
  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const loadOrderDetails = useCallback(async () => {
    if (!orderId || !outletId) return;

    try {
      setIsLoading(true);
      setError(null);
      const response = await orderApi.getOrderDetails(outletId, orderId);
      
      if (response.success && response.data) {
        setOrder(response.data.order);
      } else {
        throw new Error(response.message || 'Không thể tải chi tiết đơn hàng');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Không thể tải chi tiết đơn hàng');
    } finally {
      setIsLoading(false);
    }
  }, [orderId, outletId]);

  useEffect(() => {
    if (isOpen && orderId && outletId) {
      loadOrderDetails();
    }
  }, [isOpen, orderId, outletId, loadOrderDetails]);

  const handleConfirmOrder = () => {
    if (!orderId) return;
    setIsConfirmModalOpen(true);
  };

  const executeConfirmOrder = async () => {
    if (!orderId) return;

    try {
      setIsConfirming(true);
      setError(null);
      
      const response = await orderApi.confirmOrder(orderId);
      
      if (response.success && response.data) {
        // Update order with new status and ETA
        setOrder(response.data.confirmed_order);
        // Show success message
        setSuccessMessage(response.message || 'Đơn hàng đã được xác nhận thành công!');
        
        // Auto hide success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 5000);
        
        // Notify parent component to refresh order list
        if (onOrderUpdated) {
          onOrderUpdated();
        }
      } else {
        throw new Error(response.message || 'Không thể xác nhận đơn hàng');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Không thể xác nhận đơn hàng');
    } finally {
      setIsConfirming(false);
      setIsConfirmModalOpen(false);
    }
  };

  const handleEditOrder = () => {
    if (!order) return;
    const message = `Gửi lại thông tin chi tiết đơn hàng ${order.order_id} để tôi điều chỉnh nhé!`;
    
    // Navigate to Assistant page with pre-filled message
    navigate('/assistant', { 
      state: { 
        initialMessage: message 
      } 
    });
  };

  const handleClose = () => {
    setOrder(null);
    setError(null);
    setIsConfirming(false);
    setSuccessMessage(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] h-[90vh] max-w-4xl max-h-[90vh] p-0 flex flex-col [&>button]:hidden rounded-lg md:rounded-xl">
        {/* Fixed Header */}
        <DialogHeader className="p-4 md:p-6 pb-3 md:pb-4 border-b">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl md:text-2xl font-bold">
                Chi tiết đơn hàng
              </DialogTitle>
              <DialogDescription className="sr-only">
                Xem chi tiết thông tin đơn hàng và thực hiện các thao tác
              </DialogDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="h-8 w-8 p-0"
            >
              <Icon name="x" size={16} />
            </Button>
          </div>
        </DialogHeader>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          {/* Success Message */}
          {successMessage && (
            <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-center">
                <Icon name="check-circle" size={20} className="text-green-600 dark:text-green-400 mr-2" />
                <p className="text-green-800 dark:text-green-200 font-medium">{successMessage}</p>
              </div>
            </div>
          )}

          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-2">Đang tải...</span>
            </div>
          )}

          {error && (
            <Card className="text-center py-12">
              <CardContent>
                <Icon name="alert-circle" size={48} className="mx-auto text-red-500 mb-4" />
                <h3 className="text-lg font-semibold mb-2">Lỗi tải dữ liệu</h3>
                <p className="text-muted-foreground mb-4">{error}</p>
                <Button onClick={loadOrderDetails}>
                  Thử lại
                </Button>
              </CardContent>
            </Card>
          )}

          {order && (
            <div className="space-y-4 md:space-y-6">
            {/* Order Header */}
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <CardTitle className="text-xl">#{order.order_id}</CardTitle>
                      <Badge className={`${getOrderStatusColor(order.order_status)} text-sm px-3 py-1`}>
                        {getOrderStatusText(order.order_status)}
                      </Badge>
                    </div>
                    <div className="space-y-1 text-sm text-muted-foreground">
                      <p>Ngày đặt: {formatOrderDate(order.created_at)}</p>
                      {order.eta && (
                        <p>Dự kiến giao: {formatOrderDate(order.eta)}</p>
                      )}
                      {order.delivery_date && (
                        <p>Đã giao: {formatOrderDate(order.delivery_date)}</p>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Order Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              <Card>
                <CardContent className="p-3 md:p-4 text-center">
                  <Icon name="package" size={20} className="mx-auto text-blue-500 mb-1 md:mb-2" />
                  <div className="text-xl md:text-2xl font-bold">
                    {order.items ? order.items.length : order.items_count}
                  </div>
                  <div className="text-xs md:text-sm text-muted-foreground">Mặt hàng</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 md:p-4 text-center">
                  <Icon name="dollar-sign" size={20} className="mx-auto text-gray-500 mb-1 md:mb-2" />
                  <div className="text-xl md:text-2xl font-bold">
                    {formatCurrency(order.total_amount)}
                  </div>
                  <div className="text-xs md:text-sm text-muted-foreground">Tổng tiền</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 md:p-4 text-center">
                  <Icon name="minus" size={20} className="mx-auto text-orange-500 mb-1 md:mb-2" />
                  <div className="text-xl md:text-2xl font-bold">
                    {formatCurrency(order.discount_amount)}
                  </div>
                  <div className="text-xs md:text-sm text-muted-foreground">Giảm giá</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 md:p-4 text-center">
                  <Icon name="check-circle" size={20} className="mx-auto text-green-500 mb-1 md:mb-2" />
                  <div className="text-xl md:text-2xl font-bold text-green-600 dark:text-green-400">
                    {formatCurrency(order.final_amount)}
                  </div>
                  <div className="text-xs md:text-sm text-muted-foreground">Thành tiền</div>
                </CardContent>
              </Card>
            </div>

            {/* Order Items */}
            <div className="space-y-2 md:space-y-4 pt-4">
              <h3 className="text-lg md:text-xl font-semibold flex items-center ml-2">
                <Icon name="list" size={20} className="mr-2" />
                <span>Chi tiết các sản phẩm</span>
              </h3>
              <div className="space-y-4">
                  {/* Order Info */}
                  <div className="grid grid-cols-2 gap-4 p-4 bg-secondary rounded-lg">
                    <div>
                      <p className="text-sm text-muted-foreground">Số lần chỉnh sửa</p>
                      <p className="font-medium">{order.modification_count}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Cập nhật lần cuối</p>
                      <p className="font-medium">{formatOrderDate(order.updated_at)}</p>
                    </div>
                  </div>
                  
                  {order.items && order.items.length > 0 && (
                    <div className="space-y-3">
                      {order.items.map((item, index) => (
                        <div key={item.sku || index} className="flex items-start justify-between p-4 border rounded-lg hover:bg-accent">
                          <div className="flex-1">
                            <h5 className="font-medium text-base mb-1">{item.name}</h5>
                            <p className="text-sm text-muted-foreground mb-1">
                              Mã: {item.sku}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {item.category}
                            </p>
                            <p className="text-sm text-muted-foreground mb-1">
                              {item.packaging.description}
                            </p>
                          </div>
                          <div className="text-right ml-4">
                            <div className="font-medium text-sm mb-1">
                              {item.quantity} {item.packaging.unit}
                            </div>
                            <div className="text-sm text-muted-foreground mb-1">
                              {formatCurrency(item.unit_price)}/đơn vị
                            </div>
                            <div className="font-bold text-green-600 dark:text-green-400 text-base">
                              {formatCurrency(item.total_price)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
            </div>

            {/* Order Notes */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Icon name="message-square" size={20} />
                  <span className="text-lg md:text-xl">Ghi chú</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  {order.notes || 'Không có ghi chú'}
                </p>
              </CardContent>
            </Card>

            {/* Delivery Info */}
            {order.delivery_address && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Icon name="map-pin" size={20} />
                    <span className="text-lg md:text-xl">Thông tin giao hàng</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <p><strong>Địa chỉ:</strong> {order.delivery_address}</p>
                    {order.contact_phone && (
                      <p><strong>Số điện thoại:</strong> {order.contact_phone}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            </div>
          )}
        </div>

        {/* Fixed Footer */}
        {order && (
          <div className="p-4 md:p-6 pt-3 md:pt-4 border-t bg-muted/50">
            <div className="flex gap-2 md:gap-2">
              <Button onClick={handleClose} variant="gray" className="flex-1 text-xs md:text-sm py-2 px-2">
                <Icon name="x" size={14} className="mr-1" />
                <span className="text-xs md:text-sm">Đóng</span>
              </Button>
                {isOrderPendingOrProcessing(order.order_status) && (
                 <>
                   <Button 
                     onClick={handleEditOrder}
                     variant="orange"
                     className="flex-1 text-xs md:text-sm py-2 px-2"
                   >
                     <Icon name="edit" size={14} className="mr-1" />
                     <span className="text-xs md:text-sm">Chỉnh sửa</span>
                   </Button>
                   <Button 
                     onClick={handleConfirmOrder}
                     disabled={isConfirming}
                     className="flex-1 text-xs md:text-sm py-2 px-2 bg-green-600 hover:bg-green-700"
                   >
                     {isConfirming ? (
                       <>
                         <Icon name="loader-2" size={14} className="mr-1 animate-spin" />
                         <span className="text-xs md:text-sm">Đang xác nhận...</span>
                       </>
                     ) : (
                       <>
                         <Icon name="check-circle" size={14} className="mr-1" />
                         <span className="text-xs md:text-sm">Xác nhận</span>
                       </>
                     )}
                   </Button>
                 </>
                )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* Custom Confirmation Modal */}
    <Dialog open={isConfirmModalOpen} onOpenChange={setIsConfirmModalOpen}>
      <DialogContent className="w-[90vw] max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold flex items-center">
            <Icon name="help-circle" size={20} className="mr-2 text-blue-500" />
            Xác nhận đơn hàng
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Vui lòng xác nhận thông tin trước khi tiếp tục
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <div className="text-center space-y-3">
            <div className="text-2xl">👋</div>
            <p className="text-base">
              Xin chào {user?.contact_person || user?.name || 'Bạn'}!
            </p>
            <p className="text-sm text-muted-foreground">
              Kiểm tra đơn hàng {' '}
              <span className="font-mono font-semibold text-green-600 dark:text-green-400">
                {orderId}
              </span>{' '}
              xem cần điều chỉnh gì không? Nếu đã đúng, bấm nút "Xác nhận" bên dưới {user?.contact_person || user?.name || 'Bạn'} nhé!
            </p>
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm">
              <p className="text-blue-800 dark:text-blue-200">
                Sau khi xác nhận, đơn hàng sẽ chuyển sang trạng thái "Đã xác nhận" và 
                hệ thống sẽ tự động tính toán thời gian giao hàng dự kiến cho {user?.name || 'Cửa hàng'}.
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-2 pt-4">
          <Button 
            variant="outline" 
            onClick={() => setIsConfirmModalOpen(false)}
            className="flex-1"
            disabled={isConfirming}
          >
            <Icon name="x" size={16} />
            Hủy bỏ
          </Button>
          <Button 
            onClick={executeConfirmOrder}
            disabled={isConfirming}
            className="flex-1 bg-green-600 hover:bg-green-700"
          >
            {isConfirming ? (
              <>
                <Icon name="loader-2" size={16} className="mr-2 animate-spin" />
                Đang xác nhận...
              </>
            ) : (
              <>
                <Icon name="check-circle" size={16} className="mr-2" />
                Xác nhận
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default OrderDetailsModal;
